// @flow
import React from 'react';
import styled, { type ReactComponentStyled } from 'styled-components';
import { padding, colors, fontSize, fontWeight } from '../constants';
import { XIcon } from '../icons';

type Props = {
  maxWidth?: string,
  onRemove?: Function,
  readonly?: boolean,
  status: 'error' | 'selected' | 'unavailable' | 'default',
  text: string,
  title?: string,
  hoverable?: boolean,
};

const stopEvent = (event: SyntheticEvent<HTMLElement, MouseEvent>) =>
  event.stopPropagation();

function Chip(props: Props) {
  const { onRemove, ...nonFunctionProps } = props;
  const maxWidth = props.maxWidth || '100%';

  return (
    <ChipWrapper
      {...nonFunctionProps}
      onMouseDown={stopEvent}
      maxWidth={maxWidth}
      hoverable={props.hoverable === undefined ? true : props.hoverable}
      hoverColor={props.readonly ? 'initial' : colors.lightestGrey}
    >
      <ChipText {...nonFunctionProps} onMouseDown={stopEvent}>
        {props.text}
      </ChipText>
      <ChipRemoveLink>
        {!props.readonly && (
          <CloseIcon
            size={16}
            onClick={props.onRemove}
            onMouseDown={props.onRemove}
          />
        )}
      </ChipRemoveLink>
    </ChipWrapper>
  );
}

function borderColor(props: Props) {
  switch (props.status) {
    case 'error':
      return colors.darkRed;
    case 'selected':
      return colors.darkGreenhouseGreen;
    default:
      return colors.lightGrey;
  }
}

function textColor(props: Props) {
  switch (props.status) {
    case 'error':
      return colors.darkRed;
    case 'unavailable':
      return colors.darkGrey;
    default:
      return colors.darkestGrey;
  }
}

type WrapperAttributes = {
  'data-test-role': string,
};

const ChipWrapper: ReactComponentStyled<WrapperAttributes> = styled.span.attrs({
  'data-test-role': 'react-chosen-multiselect-option',
  title: (props: Props) => props.title || props.text,
})`
  display: inline-block;
  background: ${colors.white};
  border: 1px solid ${(props: Props) => borderColor(props)};
  color: ${(props: Props) => textColor(props)};
  font-size: ${fontSize.small};
  font-weight: ${fontWeight.normal};
  border-radius: 100px;
  vertical-align: middle;
  padding: 2px 12px;

  // spacing between chips especially between rows.
  margin: 2.5px;

  ${(props) =>
    props.hoverable &&
    `
    &:hover,
    &:active,
    &:focus {
      background: ${props.hoverColor};
    }
  `}
`;

const ChipText = styled.span`
  display: inline-block;
  text-decoration: ${(props) =>
    props.status === 'unavailable' ? 'line-through' : 'initial'};
  max-width: ${(props) => props.maxWidth};
  white-space: nowrap;
  overflow-x: hidden;
  text-overflow: ellipsis;
  vertical-align: inherit;
`;

const ChipRemoveLink = styled(ChipText).attrs({
  'data-test-role': 'react-remove-option',
})`
  padding-left: ${padding.tiny};
  color: ${colors.darkGrey};
  margin-right: -6px;
  min-height: 20px;
`;

ChipRemoveLink.displayName = 'ChipRemoveLink';

const CloseIcon = styled(XIcon)`
  padding: 2px;

  &:hover {
    background: ${colors.lightBlue};
    border-radius: 50%;
  }
`;

CloseIcon.displayName = 'CloseIcon';

export { Chip };
